<template>
  <div class="container mx-auto px-3 py-6 sm:px-4 sm:py-8">
    <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-6 sm:mb-8">
      Search Wallpapers
    </h1>
    
    <!-- Search Input -->
    <div class="mb-8">
      <SearchBar
        :loading="loading"
        :suggestions="popularSearches"
        placeholder="Search for wallpapers..."
        @search="handleSearch"
        @clear="handleClearSearch"
        @suggestion-select="handleSuggestionSelect"
      />
    </div>

    <!-- Popular Search Terms -->
    <div v-if="!searchQuery && !hasSearched" class="mb-6 sm:mb-8">
      <h2 class="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">Popular Searches</h2>
      <div class="flex flex-wrap gap-2 sm:gap-3">
        <button
          v-for="term in popularSearches"
          :key="term"
          @click="handleSearch(term)"
          class="px-3 sm:px-4 py-2 sm:py-2 text-sm sm:text-base bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 active:scale-95 transition-all duration-150"
        >
          {{ term }}
        </button>
      </div>
    </div>

    <!-- Search Results -->
    <div v-if="hasSearched">
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
          {{ searchResults.length > 0 ? `Found ${searchResults.length} results for "${searchQuery}"` : `No results found for "${searchQuery}"` }}
        </h2>
        
        <!-- View Mode Toggle -->
        <div class="flex items-center space-x-2 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
          <button @click="viewMode = 'grid'" :class="[
            'px-3 py-1 text-sm rounded-md transition-colors',
            viewMode === 'grid'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          ]">
            Grid
          </button>
          <button @click="viewMode = 'preview'" :class="[
            'px-3 py-1 text-sm rounded-md transition-colors',
            viewMode === 'preview'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          ]">
            Device Preview
          </button>
        </div>
      </div>
      
      <WallpaperGrid
        :wallpapers="searchResults"
        :loading="loading"
        :has-more="false"
        :view-mode="viewMode"
        @wallpaper-click="viewWallpaper"
      />
    </div>

    <!-- Default State -->
    <div v-else class="text-center py-16">
      <svg class="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
      </svg>
      <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-2">Start searching</h3>
      <p class="text-gray-500 dark:text-gray-400">Enter keywords to find amazing wallpapers</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import SearchBar from '../components/SearchBar.vue';
import WallpaperGrid from '../components/WallpaperGrid.vue';
import { wallpaperService, type Wallpaper } from '../firebase/services/wallpaperService';

const router = useRouter();

// State
const viewMode = ref('grid'); // 'grid' or 'preview'
const searchQuery = ref('');
const searchResults = ref<Wallpaper[]>([]);
const loading = ref(false);
const hasSearched = ref(false);

// Popular search terms
const popularSearches = ref([
  'nature', 'abstract', 'space', 'city', 'mountain', 'ocean', 'sunset', 'minimalist'
]);

// Methods
const handleSearch = async (query: string) => {
  searchQuery.value = query;
  
  if (!query.trim()) {
    searchResults.value = [];
    hasSearched.value = false;
    return;
  }

  loading.value = true;
  hasSearched.value = true;
  
  try {
    const results = await wallpaperService.searchWallpapers(query.trim());
    searchResults.value = results;
  } catch (error) {
    console.error('Error searching wallpapers:', error);
    searchResults.value = [];
  } finally {
    loading.value = false;
  }
};

const handleClearSearch = () => {
  searchQuery.value = '';
  searchResults.value = [];
  hasSearched.value = false;
};

const handleSuggestionSelect = (suggestion: string) => {
  searchQuery.value = suggestion;
  handleSearch(suggestion);
};

const viewWallpaper = (wallpaper: Wallpaper) => {
  router.push(`/wallpaper/${wallpaper.id}`);
};
</script>